import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AbstractAIBackground from '../assets/abstract-ai-bg';
import './AuthPages.css';
import { authService, userService } from '../services/api';
import { Check, Eye, EyeOff, Mail, Lock, ArrowRight, Home } from 'react-feather';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const navigate = useNavigate();

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.keyCode === 27) { // ESC key
        navigate('/');
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [navigate]);

  const handleBackdropClick = (e) => {
    if (e.target.className === 'auth-backdrop') {
      navigate('/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!email || !password) {
      setError('Email and password are required');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.login(email, password);
      
      if (response && response.token) {
        try {
          const me = await userService.getUser();
          if (me && me.isInitialSetupCompleted === false) {
            setLoading(false);
            navigate('/initial-setup');
            return;
          }
        } catch (e) {
          console.warn('Could not fetch user after login, proceeding to home', e);
        }
        // Successfully logged in
        if (rememberMe) {
          // If remember me was checked, we could implement persistent login here
          // For example, set a longer token expiration
          localStorage.setItem('remember_me', 'true');
        }
        setLoading(false);
        navigate('/home');
      } else {
        throw new Error('Invalid login credentials');
      }
    } catch (err) {
      setError(err.response?.data || err.message || 'Failed to login. Please try again.');
      setLoading(false);
    }
  };

  const navigateToRegister = () => {
    navigate('/register');
  };

  // Responsive feature data
  const features = [
    {
      icon: <Check size={20} />,
      text: "Interactive knowledge graphs"
    },
    {
      icon: <Check size={20} />,
      text: "AI-powered learning insights"
    },
    {
      icon: <Check size={20} />,
      text: "Personalized progress tracking"
    }
  ];

  return (
    <div className="auth-backdrop" onClick={handleBackdropClick}>
      <div className="auth-container modern" onClick={(e) => e.stopPropagation()}>
        <div className="auth-left-panel">
          <div className="background-svg">
            <AbstractAIBackground />
          </div>
          <div className="content">
            <div className="auth-logo">
              <span>Brainy</span>
            </div>
            <button onClick={() => navigate('/')} className="back-to-site modern">
              <Home size={18} />
              <span>Back to site</span>
            </button>
            <div className="auth-tagline">
              <h2>Welcome back</h2>
              <p>Sign in to continue your learning journey and unlock your potential with AI.</p>
            </div>
            <div className="auth-features">
              {features.map((feature, index) => (
                <div key={index} className="auth-feature-card">
                  <div className="auth-feature-icon">
                    {feature.icon}
                  </div>
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="auth-right-panel">
          <div className="auth-form-container modern">
            <div className="auth-header">
              <h1>Welcome back</h1>
              <p>Sign in to your account to continue</p>
            </div>
            
            {error && (
              <div className="auth-error modern">
                <div className="error-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </div>
                <span>{error}</span>
              </div>
            )}
            
            <form className="auth-form modern" onSubmit={handleSubmit}>
              <div className="input-group modern">
                <label htmlFor="email">Email address</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="modern-input"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                </div>
              </div>
              
              <div className="input-group modern">
                <label htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <Lock size={18} />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="modern-input"
                    autoComplete="current-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  <button 
                    type="button"
                    className="password-toggle modern"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div className="auth-form-actions modern">
                <label className="remember-me modern">
                  <input 
                    type="checkbox" 
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">Remember me for 30 days</span>
                </label>
                <button type="button" className="forgot-password modern">
                  Forgot password?
                </button>
              </div>
              
              <button type="submit" className="auth-submit-btn modern" disabled={loading}>
                {loading ? (
                  <>
                    <div className="auth-loading-spinner"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign in</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
            
            <div className="auth-divider modern">
              <span>or</span>
            </div>
            
            <div className="register-link modern">
              <span>New to Brainy?</span>
              <button onClick={navigateToRegister} className="link-button modern">
                Create an account
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 